'use strict';

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Helper functions for input validation
 */

/**
 * Check if a character is numeric (digit, decimal point, or comma)
 * @param {string} char - Character to check
 * @returns {boolean}
 */
const isNumericChar = (char) => /^[\d.,]$/.test(char);

/**
 * Check if a character is a digit (0-9)
 * @param {string} char - Character to check
 * @returns {boolean}
 */
const isDigit = (char) => /^\d$/.test(char);

/**
 * Check if a character is a decimal point (. or ,)
 * @param {string} char - Character to check
 * @returns {boolean}
 */
const isDecimalPoint = (char) => char === '.' || char === ',';

/**
 * Check if a digit should be blocked based on decimal limit and cursor position
 * @param {string} currentValue - Current input value
 * @param {number} decimals - Maximum decimal places allowed
 * @param {number} cursorPos - Current cursor position
 * @returns {boolean} - True if digit should be blocked
 */
const shouldBlockDigit = (currentValue, decimals, cursorPos) => {
    const parts = currentValue.split('.');

    // If no decimal point or decimals = 0, allow
    if (parts.length !== 2 || decimals === 0) {
        return false;
    }

    const decimalPos = currentValue.indexOf('.');

    // If cursor is after decimal and we've reached limit, block
    return cursorPos > decimalPos && parts[1].length >= decimals;
};

/**
 * DecimalInput - A controlled input component that accepts decimal values with configurable precision
 *
 * Features:
 * - Allows decimal values with configurable decimal places (default: 1)
 * - Blocks non-numeric characters except decimal point
 * - Prevents multiple decimal points
 * - Optional min/max validation
 * - HTML5 validation with pattern
 * - Automatically rounds values exceeding decimal precision
 * - Works with both metric and imperial units
 */
const DecimalInput = ({
    value,
    onChange,
    onError,
    onFocus,
    onBlur,
    min,
    max,
    decimals = 1,
    placeholder,
    disabled,
    autoFocus,
    className,
    name,
    id,
    tabIndex,
    'data-testid': dataTestId
}) => {
    const [error, setError] = useState(null);

    const validationMessage = `Must be a number${decimals > 0 ? ` with up to ${decimals} decimal place${decimals > 1 ? 's' : ''}` : ''}${min !== undefined ? ` (min: ${min})` : ''}${max !== undefined ? ` (max: ${max})` : ''}`;

    // Sanitize value BEFORE render to prevent visual flash
    const sanitizedValue = useMemo(() => {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        // Preserve trailing decimal point during typing (e.g., "89.")
        if (typeof value === 'string' && value.endsWith('.')) {
            return value;
        }

        // Preserve leading decimal point alone (e.g., ".")
        if (value === '.') {
            return value;
        }

        // Preserve leading decimal with digits during typing (e.g., ".5")
        if (typeof value === 'string' && value.startsWith('.') && value.length > 1) {
            return value;
        }

        const numValue = typeof value === 'string' ? parseFloat(value) : value;

        // If value has more decimals than allowed, round to specified decimal places
        if (!isNaN(numValue) && decimals >= 0) {
            const multiplier = Math.pow(10, decimals);
            const rounded = Math.round(numValue * multiplier) / multiplier;

            // Return as string to preserve decimal places (e.g., "55.0" instead of "55")
            return decimals === 0 ? Math.round(numValue) : rounded;
        }

        return value;
    }, [value, decimals]);

    const handleChange = (e) => {
        let inputValue = e.target.value;

        // Allow empty value
        if (inputValue === '') {
            setError(null);
            onChange && onChange('');
            return;
        }

        // Normalize comma to period for decimal separator
        inputValue = inputValue.replace(',', '.');

        // Remove any characters that are not digits or decimal point
        inputValue = inputValue.replace(/[^\d.]/g, '');

        // Ensure only one decimal point
        const parts = inputValue.split('.');
        if (parts.length > 2) {
            inputValue = parts[0] + '.' + parts.slice(1).join('');
        }

        // If decimals is 0, remove any decimal point
        if (decimals === 0) {
            inputValue = inputValue.replace(/\./g, '');
        }

        // Limit decimal places
        if (parts.length === 2 && decimals > 0) {
            if (parts[1].length > decimals) {
                // Trim excess decimals
                parts[1] = parts[1].substring(0, decimals);
                inputValue = parts.join('.');
            }
        }

        // If nothing left after sanitization, treat as empty
        if (inputValue === '') {
            setError(null);
            onChange && onChange('');
            return;
        }

        // Allow decimal point alone (will be treated as "0." in validation)
        if (inputValue === '.') {
            setError(null);
            onChange && onChange(inputValue);
            return;
        }

        // Parse value - treat "." as "0." for validation
        const valueForParsing = inputValue === '.' ? '0.' : inputValue;
        const numValue = parseFloat(valueForParsing);

        // Check if it's a valid number
        if (isNaN(numValue)) {
            setError('Invalid number');
            onError && onError('Invalid number');
            return;
        }

        // Validate min/max
        if (min !== undefined && numValue < min) {
            const errorMsg = `Value must be at least ${min}`;
            setError(errorMsg);
            onError && onError(errorMsg);
            return;
        }

        if (max !== undefined && numValue > max) {
            const errorMsg = `Value must be at most ${max}`;
            setError(errorMsg);
            onError && onError(errorMsg);
            return;
        }

        // Valid number
        setError(null);
        onError && onError(null);

        // Return the raw input value to preserve trailing zeros and partial decimals
        // e.g., "55." or "55.0" during typing
        onChange && onChange(inputValue);
    };

    const handleBeforeInput = (e) => {
        const currentValue = e.target.value || '';
        const newChar = e.data;

        if (!newChar) return;

        // Block non-numeric characters (except decimal point)
        if (!isNumericChar(newChar)) {
            e.preventDefault();
            return;
        }

        // Block decimal point if decimals = 0
        if (isDecimalPoint(newChar) && decimals === 0) {
            e.preventDefault();
            return;
        }

        // Block second decimal point
        if (isDecimalPoint(newChar) && currentValue.includes('.')) {
            e.preventDefault();
            return;
        }

        // Block typing more digits after decimal limit is reached
        if (isDigit(newChar)) {
            const cursorPos = e.target.selectionStart;

            if (shouldBlockDigit(currentValue, decimals, cursorPos)) {
                e.preventDefault();
                return;
            }
        }
    };

    const handleKeyPress = (e) => {
        // Fallback for browsers that don't support onBeforeInput
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];

        if (allowedKeys.includes(e.key)) {
            return;
        }

        // Block non-digit characters (except decimal)
        if (!/^[\d.,]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e) => {
        // Get pasted data
        const pastedData = e.clipboardData.getData('text');

        // Normalize comma to period
        const normalized = pastedData.replace(',', '.');

        // Check if it's a valid number format
        const numberPattern = decimals === 0 ? /^\d+$/ : /^\d+\.?\d*$/;

        if (!numberPattern.test(normalized)) {
            e.preventDefault();
            return;
        }

        // If decimals allowed, check decimal places
        if (decimals > 0 && normalized.includes('.')) {
            const parts = normalized.split('.');
            if (parts[1].length > decimals) {
                // Will be handled by onChange
                return;
            }
        }
    };

    // Generate pattern based on decimals
    const pattern = decimals === 0
        ? '\\d+'
        : `\\d+(\\.\\d{0,${decimals}})?`;

    // ARIA label for accessibility
    const ariaLabel = placeholder || `Decimal input with up to ${decimals} decimal place${decimals !== 1 ? 's' : ''}`;

    return (
        <input
            type="text"
            id={id}
            name={name}
            value={sanitizedValue}
            onChange={handleChange}
            onBeforeInput={handleBeforeInput}
            onKeyDown={handleKeyPress}
            onPaste={handlePaste}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            tabIndex={tabIndex}
            className={className}
            inputMode="decimal"
            pattern={pattern}
            aria-label={ariaLabel}
            aria-invalid={error !== null}
            data-testid={dataTestId}
        />
    );
};

DecimalInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func,
    onError: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    decimals: PropTypes.number,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    name: PropTypes.string,
    id: PropTypes.string,
    tabIndex: PropTypes.number,
    'data-testid': PropTypes.string,
};

DecimalInput.defaultProps = {
    value: '',
    decimals: 1,
    disabled: false,
    autoFocus: false,
};

export default DecimalInput;
